import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { errorMessage, post, put } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EMPTY = { partner_name: "", offer_title: "", description: "", code: "", terms: "", url: "", active: true };

export const PartnerOfferDialog = ({ open, onOpenChange, offer, onSaved }) => {
  const [fields, setFields] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setFields(offer ? { ...EMPTY, ...offer } : EMPTY);
  }, [open, offer]);

  const save = async (event) => {
    event.preventDefault();
    setBusy(true);
    const body = Object.fromEntries(Object.entries(fields).map(([key, value]) => [key, typeof value === "string" ? value.trim() || null : value]));
    try {
      const saved = offer ? await put(`/platform/partners/${offer.id}`, body) : await post("/platform/partners", body);
      toast.success(offer ? "Partner offer updated" : "Partner offer added");
      onSaved(saved);
      onOpenChange(false);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  const set = (key) => (event) => setFields((current) => ({ ...current, [key]: event.target.value }));
  return (
    <Dialog open={open} onOpenChange={(value) => !busy && onOpenChange(value)}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-xl" data-testid="partner-offer-dialog">
        <DialogHeader><DialogTitle className="font-display">{offer ? "Edit partner offer" : "Add partner offer"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4" data-testid="partner-offer-form">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="partner-name">Partner name *</Label>
              <Input id="partner-name" value={fields.partner_name} onChange={set("partner_name")} maxLength={120} required className="mt-1.5" data-testid="partner-offer-name" />
            </div>
            <div>
              <Label htmlFor="partner-title">Offer title *</Label>
              <Input id="partner-title" value={fields.offer_title} onChange={set("offer_title")} maxLength={160} required className="mt-1.5" placeholder="15% off your first order" data-testid="partner-offer-title" />
            </div>
          </div>
          <div>
            <Label htmlFor="partner-description">Description *</Label>
            <Textarea id="partner-description" value={fields.description} onChange={set("description")} maxLength={2000} rows={3} required className="mt-1.5" data-testid="partner-offer-description" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="partner-code">Discount code</Label>
              <Input id="partner-code" value={fields.code || ""} onChange={set("code")} maxLength={100} className="mt-1.5" data-testid="partner-offer-code" />
            </div>
            <div>
              <Label htmlFor="partner-url">Partner website</Label>
              <Input id="partner-url" type="url" value={fields.url || ""} onChange={set("url")} maxLength={500} placeholder="https://example.com" className="mt-1.5" data-testid="partner-offer-url" />
            </div>
          </div>
          <div>
            <Label htmlFor="partner-terms">How to redeem / terms</Label>
            <Textarea id="partner-terms" value={fields.terms || ""} onChange={set("terms")} maxLength={1000} rows={3} className="mt-1.5" data-testid="partner-offer-terms" />
          </div>
          <label className="flex cursor-pointer items-center gap-3 rounded-md border border-slate-200 p-3 text-sm font-medium text-slate-800">
            <Checkbox checked={fields.active} onCheckedChange={(active) => setFields((current) => ({ ...current, active: !!active }))} data-testid="partner-offer-active" />
            Published to logged-in users
          </label>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-blue-700 hover:bg-blue-800" data-testid="partner-offer-save">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{offer ? "Save changes" : "Add offer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};